import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import { useIntl } from "react-intl";
import moment from "moment";
import { advertisementsApiService } from "../../../cores/advertisements/services/api";
import type { Ad } from "../../../cores/advertisements/interfaces/api/get-ads";
import type { Campaign } from "../../../cores/advertisements/interfaces/api/get-campaigns";
import { CampaignStatus } from "../../../cores/advertisements/constants/campaign-status";
import { useAdvertisementCore } from "../../../cores/advertisements";
import { useFacebookAdsCore } from "../../../cores/facebookAds";
import { useMeCore } from "../../../cores/me";
import ActivateButton from "./ActivateButton";
import "./CampaignsTable.scss";

function columnCellStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    flex: "0 0 auto",
  };
}

function formatMoney(
  n: number | undefined | null,
  currency: string | undefined
): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const code =
    currency && /^[A-Z]{3}$/i.test(currency)
      ? currency.toUpperCase()
      : "USD";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n));
  } catch {
    return String(n);
  }
}

function formatObjective(raw?: string | null): string {
  if (!raw || !String(raw).trim()) return "—";
  return String(raw).replace(/_/g, " ");
}

export type FlatAdRow = Ad & {
  campaignId: string;
  campaignName: string;
  adsetId: string;
  adsetName: string;
};

type Props = {
  campaigns: Campaign[];
};

const COLS = [
  { key: "ad", width: 340 },
  { key: "platform", width: 100 },
  { key: "format", width: 120 },
  { key: "campaign", width: 200 },
  { key: "adset", width: 220 },
  { key: "spend", width: 160 },
] as const;

export default function AdsFlatTable({ campaigns }: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const { settings } = useFacebookAdsCore();
  const { putAd } = useAdvertisementCore();
  const [rows, setRows] = useState<FlatAdRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    if (!company?.id || !campaigns.length) {
      setRows([]);
      setLoadError(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    const merged: FlatAdRow[] = [];
    try {
      for (const c of campaigns) {
        const { adsets } = await advertisementsApiService.getAdsets(
          company.id,
          c.id
        );
        for (const adset of adsets) {
          const { ads } = await advertisementsApiService.getAds(
            company.id,
            adset.id
          );
          for (const ad of ads) {
            merged.push({
              ...ad,
              campaignId: c.id,
              campaignName: c.name,
              adsetId: adset.id,
              adsetName: adset.name,
            });
          }
        }
      }
      setRows(merged);
      setLoadError(false);
    } catch (e: unknown) {
      console.error(e);
      setRows([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [campaigns, company?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.campaignName || "").toLowerCase().includes(q) ||
        (r.adsetName || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const handleToggleAd = useCallback(
    (row: FlatAdRow) => {
      const end = campaigns.find((c) => c.id === row.campaignId)?.stopTime;
      if (!end || moment().isBefore(end)) {
        if (row.status === CampaignStatus.ACTIVE.id) {
          putAd(company?.id!, row.id, CampaignStatus.PAUSED.id);
        } else if (row.status === CampaignStatus.PAUSED.id) {
          putAd(company?.id!, row.id, CampaignStatus.ACTIVE.id);
        }
      }
    },
    [campaigns, company?.id, putAd]
  );

  return (
    <div className="overflow-auto h-100 fb-campaigns-table">
      <div className="fb-campaigns-table__toolbar">
        <div className="fb-campaigns-table__toolbar-start">
          <div className="fb-campaigns-table__search-wrap">
            <FontAwesomeIcon
              className="fb-campaigns-table__search-icon"
              icon={["fas", "search"]}
            />
            <input
              type="search"
              className="form-control fb-campaigns-table__search"
              placeholder={intl.formatMessage({
                id: "app.advertisements.tableSearchPlaceholder",
              })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <button
          type="button"
          className="fb-campaigns-table__icon-btn"
          onClick={() => load()}
          title={intl.formatMessage({
            id: "app.advertisements.toolbar.refresh",
          })}
        >
          <FontAwesomeIcon icon={["fas", "sync-alt"]} />
        </button>
        <span className="fb-campaigns-table__meta">
          {intl.formatMessage(
            { id: "app.advertisements.adsFlatCount" },
            { count: filtered.length }
          )}
        </span>
      </div>

      <div className="fb-campaigns-table__head-row fb-campaigns-table__head-row--ads">
        {COLS.map((c) => (
          <div
            key={c.key}
            className="header fb-campaigns-table__col-head"
            style={columnCellStyle(c.width)}
          >
            {intl.formatMessage({
              id: `app.advertisements.adsCol.${c.key}`,
            })}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="fb-campaigns-table__empty">
          {intl.formatMessage({ id: "app.advertisements.flatLoading" })}
        </div>
      ) : loadError ? (
        <div className="fb-campaigns-table__flat-error">
          <p className="fb-campaigns-table__flat-error-text">
            {intl.formatMessage({
              id: "app.advertisements.flatLoadErrorAds",
              defaultMessage:
                "We couldn't load ads. If Meta is rate-limiting, wait a few minutes and tap Retry.",
            })}
          </p>
          <button
            type="button"
            className="fb-campaigns-table__flat-retry"
            onClick={() => load()}
          >
            {intl.formatMessage({
              id: "app.advertisements.flatRetry",
              defaultMessage: "Retry",
            })}
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="fb-campaigns-table__empty">
          {intl.formatMessage({ id: "app.advertisements.adsFlatEmpty" })}
        </div>
      ) : (
        filtered.map((row, idx) => (
          <div
            className="table fb-campaigns-table__campaign"
            key={row.id || `ad-${idx}`}
          >
            <div className="table-row fb-campaigns-table__row fb-campaigns-table__row--ads">
              {COLS.map((col) => (
                <div
                  key={col.key}
                  className={cn(
                    "table-cell fb-campaigns-table__cell",
                    col.key === "ad" &&
                      "justify-content-start fb-campaigns-table__name-cell fb-campaigns-table__cell--ad-primary"
                  )}
                  style={columnCellStyle(col.width)}
                >
                  {col.key === "ad" && (
                    <div className="fb-campaigns-table__ad-primary">
                      <span
                        className="fb-campaigns-table__ad-status-wrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActivateButton
                          status={row.status}
                          endDate={
                            campaigns.find((c) => c.id === row.campaignId)
                              ?.stopTime ?? ""
                          }
                          onClick={() => handleToggleAd(row)}
                        />
                      </span>
                      <div className="fb-campaigns-table__thumb-wrap" aria-hidden>
                        {row.imageUrl ? (
                          <img
                            src={row.imageUrl}
                            alt=""
                            className="fb-campaigns-table__thumb-img"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="fb-campaigns-table__thumb-placeholder">
                            <FontAwesomeIcon icon={["fas", "image"]} />
                          </div>
                        )}
                      </div>
                      <span className="fb-campaigns-table__name-text">
                        {row.name}
                      </span>
                    </div>
                  )}
                  {col.key === "platform" && (
                    <span className="fb-campaigns-table__meta-platform">
                      <FontAwesomeIcon icon={["fab", "meta"]} />
                    </span>
                  )}
                  {col.key === "format" && formatObjective(row.objective)}
                  {col.key === "campaign" && row.campaignName}
                  {col.key === "adset" && (
                    <div className="fb-campaigns-table__adset-col">
                      <span className="fb-campaigns-table__adset-name">
                        {row.adsetName}
                      </span>
                      <span className="fb-campaigns-table__adset-sub">
                        {intl.formatMessage({
                          id: "app.advertisements.adsCol.adsetSub",
                        })}
                      </span>
                    </div>
                  )}
                  {col.key === "spend" &&
                    formatMoney(row.spend, settings?.currency)}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
